import { useAccount } from "$src/stores/use-account";
import { useCallback, useMemo } from "react";
import { useLocalStorage } from "usehooks-ts";
import { v4 as uuidv4 } from "uuid";

export type CustomFilter = {
  id: string;
  name: string;
  demographics: string[];
  default: boolean;
};

export const useSaveCustomFilter = () => {
  const [allFilters, setAllFilters] = useLocalStorage<
    Record<string, CustomFilter[]>
  >("custom-filters", {});
  const accountBrand = useAccount((s) => s.active);

  const filters = useMemo(() => {
    return allFilters[accountBrand?.accountBrandId ?? ""] ?? [];
  }, [accountBrand, allFilters]);

  const setFilters = useCallback(
    (filters: CustomFilter[]) => {
      accountBrand?.accountBrandId &&
        setAllFilters({
          ...allFilters,
          [accountBrand.accountBrandId]: filters,
        });
    },
    [accountBrand, setAllFilters],
  );

  const add = (filter: Omit<CustomFilter, "id">) => {
    const oldFilters = filters.map((f) =>
      filter.default ? { ...f, default: false } : f,
    );
    setFilters([...oldFilters, { ...filter, id: uuidv4() }]);
  };

  const remove = (filterId: string) => {
    setFilters(filters.filter((f) => f.id !== filterId));
  };

  const update = (filter: CustomFilter) => {
    setFilters(filters.map((f) => (f.id === filter.id ? filter : f)));
  };

  const clear = () => {
    setFilters([]);
  };

  const clearAll = () => {
    setAllFilters({});
  };

  const get = (id: string) => {
    return filters.find((f) => f.id === id);
  };

  const setDefault = (filter: CustomFilter) => {
    setFilters(filters.map((f) => ({ ...f, default: f.id === filter.id })));
  };

  const getDefault = () => {
    return filters.find((f) => f.default);
  };

  return {
    filters,
    add,
    update,
    remove,
    clear,
    clearAll,
    get,
    getDefault,
    setDefault,
  };
};
